import React, { useState, useEffect } from 'react';
import performanceMonitor from './utils/performanceMonitor';
import pagePerformanceMonitor from './utils/pagePerformanceMonitor';
import AADHeader from './components/AADHeader.js';
import SideNavigation from './components/SideNavigation.js';
import './PerformanceDashboard.css';

function PerformanceDashboardPage() {
  const [metrics, setMetrics] = useState({});
  const [pageMetrics, setPageMetrics] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pages');
  const [exportFormat, setExportFormat] = useState('json');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    // Make monitors globally accessible
    window.performanceMonitor = performanceMonitor;
    window.pagePerformanceMonitor = pagePerformanceMonitor;
    
    // Initial load
    loadMetrics();
    setIsLoading(false);
    
    // Listen for automatic updates from page performance monitor
    const handlePageMetricsUpdate = () => {
      console.log('📊 Dashboard update received from page performance monitor');
      loadMetrics();
    };
    
    window.addEventListener('pageMetricsUpdated', handlePageMetricsUpdate);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('pageMetricsUpdated', handlePageMetricsUpdate);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadMetrics, 2000); // Refresh every 2 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadMetrics = () => {
    try {
      const dashboardMetrics = performanceMonitor.getDashboardMetrics();
      const pageReport = pagePerformanceMonitor.generatePageReport();
      setMetrics(dashboardMetrics);
      setPageMetrics(pageReport);
    } catch (error) {
      console.error('Error loading metrics:', error);
      // Set empty metrics to prevent dashboard from breaking
      setMetrics({});
      setPageMetrics({});
    }
  };

  const getPerformanceColor = (duration) => {
    if (duration <= 200) return '#10B981'; // Green
    if (duration <= 500) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const formatMetricName = (name) => {
    return name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const getMetricType = (label) => {
    if (label.includes('api') || label.includes('fetch') || label.includes('update') || label.includes('save')) {
      return 'API';
    }
    return 'Frontend';
  };

  const getThreshold = (type) => {
    return type === 'API' ? 1000 : 500;
  };

  const exportPerformanceData = () => {
    let data;
    
    if (exportFormat === 'json') {
      data = JSON.stringify({
        performanceMetrics: metrics,
        pageMetrics: pageMetrics
      }, null, 2);
    } else if (exportFormat === 'csv') {
      data = convertToCSV();
    }
    
    const blob = new Blob([data], { 
      type: exportFormat === 'json' ? 'application/json' : 'text/csv' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const convertToCSV = () => {
    const rows = [];
    
    // Add headers for page metrics
    rows.push(['Page Name', 'Load Time (ms)', 'Frontend Duration (ms)', 'API Duration (ms)', 'Total Duration (ms)', 'Performance', 'Interactions', 'API Calls', 'Errors', 'Last Visited']);
    
    // Add page data rows
    Object.entries(pageMetrics.pages || {}).forEach(([pageName, data]) => {
      rows.push([
        pageName,
        data.loadTime.toFixed(2),
        data.frontendDuration.toFixed(2),
        data.apiDuration.toFixed(2),
        data.totalDuration.toFixed(2),
        data.performance,
        data.interactions,
        data.apiCalls,
        data.errors,
        new Date(data.lastVisited).toLocaleString()
      ]);
    });
    
    return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  };

  const clearAllMetrics = () => {
    performanceMonitor.clearMetrics();
    pagePerformanceMonitor.clearPageMetrics();
    loadMetrics();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading-spinner">Loading Performance Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="performance-dashboard-wrapper">
      {/* Header */}
      <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
      
      {/* Sidebar */}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      
      {/* Main Content */}
      <div className={`performance-dashboard-main ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="performance-dashboard-page">
          {/* Dashboard Header */}
          <div className="dashboard-header">
            <h1>📊 Comprehensive Performance Dashboard</h1>
            <div className="header-controls">
              <button 
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`control-btn ${autoRefresh ? 'active' : ''}`}
              >
                {autoRefresh ? '🔄 Auto Refresh ON' : '⏸️ Auto Refresh OFF'}
              </button>
              <button onClick={loadMetrics} className="control-btn">
                🔄 Refresh Now
              </button>
              <button onClick={() => {
                performanceMonitor.logReport();
                pagePerformanceMonitor.logPageReport();
              }} className="control-btn">
                📋 Console Report
              </button>
              <button onClick={clearAllMetrics} className="control-btn danger">
                🗑️ Clear All
              </button>
            </div>
          </div>

          {/* Session Information */}
          {pageMetrics.session && (
            <div className="session-info">
              <h3>🔍 Session Information</h3>
              <div className="session-grid">
                <div className="session-item">
                  <strong>Session ID:</strong> {pageMetrics.session.sessionId}
                </div>
                <div className="session-item">
                  <strong>Start Time:</strong> {new Date(pageMetrics.session.startTime).toLocaleString()}
                </div>
                <div className="session-item">
                  <strong>Duration:</strong> {pageMetrics.session.duration}ms
                </div>
                <div className="session-item">
                  <strong>Total Pages:</strong> {pageMetrics.session.totalPages}
                </div>
                <div className="session-item">
                  <strong>Current Page:</strong> {pagePerformanceMonitor.getCurrentPageName()}
                </div>
                <div className="session-item">
                  <strong>URL:</strong> {window.location.href}
                </div>
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          {pageMetrics.summary && Object.keys(pageMetrics.summary).length > 0 && (
            <div className="summary-section">
              <h3>📈 Summary Statistics</h3>
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.totalPages}</div>
                  <div className="summary-label">Total Pages</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.averageLoadTime.toFixed(2)}ms</div>
                  <div className="summary-label">Average Load Time</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.averageFrontendDuration.toFixed(2)}ms</div>
                  <div className="summary-label">Avg Frontend Duration</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.averageApiDuration.toFixed(2)}ms</div>
                  <div className="summary-label">Avg API Duration</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.fastestPage.toFixed(2)}ms</div>
                  <div className="summary-label">Fastest Page</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.slowestPage.toFixed(2)}ms</div>
                  <div className="summary-label">Slowest Page</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.totalInteractions}</div>
                  <div className="summary-label">Total Interactions</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.totalApiCalls}</div>
                  <div className="summary-label">Total API Calls</div>
                </div>
                <div className="summary-card">
                  <div className="summary-value">{pageMetrics.summary.totalErrors}</div>
                  <div className="summary-label">Total Errors</div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'pages' ? 'active' : ''}`}
              onClick={() => setActiveTab('pages')}
            >
              📄 Page Metrics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              📋 All Metrics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              🌐 API Metrics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'frontend' ? 'active' : ''}`}
              onClick={() => setActiveTab('frontend')}
            >
              ⚡ Frontend Metrics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'export' ? 'active' : ''}`}
              onClick={() => setActiveTab('export')}
            >
              📤 Export Data
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Page Metrics Tab */}
            {activeTab === 'pages' && (
              <div className="metrics-table-container">
                <h3>📄 Page Performance Metrics</h3>
                {pageMetrics.pages && Object.keys(pageMetrics.pages).length > 0 ? (
                  <div className="table-wrapper">
                    <table className="metrics-table">
                      <thead>
                        <tr>
                          <th>Page Name</th>
                          <th>Load Time (ms)</th>
                          <th>Frontend Duration (ms)</th>
                          <th>API Duration (ms)</th>
                          <th>Total Duration (ms)</th>
                          <th>Performance</th>
                          <th>Interactions</th>
                          <th>API Calls</th>
                          <th>Errors</th>
                          <th>Last Visited</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pageMetrics.pages).map(([pageName, data]) => (
                          <tr key={pageName}>
                            <td className="operation-name">{pageName}</td>
                            <td style={{ color: getPerformanceColor(data.loadTime) }}>
                              {data.loadTime.toFixed(2)}
                            </td>
                            <td style={{ color: getPerformanceColor(data.frontendDuration) }}>
                              {data.frontendDuration.toFixed(2)}
                            </td>
                            <td style={{ color: getPerformanceColor(data.apiDuration) }}>
                              {data.apiDuration.toFixed(2)}
                            </td>
                            <td style={{ color: getPerformanceColor(data.totalDuration) }}>
                              {data.totalDuration.toFixed(2)}
                            </td>
                            <td>
                              <span className={`performance-badge ${data.performance.toLowerCase()}`}>
                                {data.performance}
                              </span>
                            </td>
                            <td>{data.interactions}</td>
                            <td>{data.apiCalls}</td>
                            <td>
                              <span style={{ color: data.errors > 0 ? '#EF4444' : '#10B981' }}>
                                {data.errors}
                              </span>
                            </td>
                            <td>{new Date(data.lastVisited).toLocaleTimeString()}</td>
                            <td>
                              <button 
                                onClick={() => showPageDetails(pageName, data)}
                                className="detail-btn"
                              >
                                📊 Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">
                    <p>No page metrics recorded yet.</p>
                    <p>Navigate to different pages to start collecting data.</p>
                    <button onClick={() => window.location.href = '/listofentities'} className="control-btn">
                      🚀 Go to List of Entities
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* All Metrics Tab */}
            {activeTab === 'summary' && (
              <div className="metrics-table-container">
                <h3>📋 All Performance Metrics</h3>
                {pageMetrics.detailedMetrics && Object.keys(pageMetrics.detailedMetrics).length > 0 ? (
                  <div className="table-wrapper">
                    <table className="metrics-table">
                      <thead>
                        <tr>
                          <th>Operation</th>
                          <th>Type</th>
                          <th>Count</th>
                          <th>Average (ms)</th>
                          <th>Min (ms)</th>
                          <th>Max (ms)</th>
                          <th>Median (ms)</th>
                          <th>Success Rate (%)</th>
                          <th>Performance</th>
                          <th>Last Executed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pageMetrics.detailedMetrics).map(([label, data]) => (
                          <tr key={label}>
                            <td className="operation-name">{label}</td>
                            <td>
                              <span className={`type-badge ${getMetricType(label).toLowerCase()}`}>
                                {getMetricType(label)}
                              </span>
                            </td>
                            <td>{data.count}</td>
                            <td style={{ color: getPerformanceColor(parseFloat(data.average)) }}>
                              {data.average}
                            </td>
                            <td>{data.min}</td>
                            <td>{data.max}</td>
                            <td>{data.median}</td>
                            <td>
                              <span style={{ color: parseFloat(data.successRate) >= 95 ? '#10B981' : '#EF4444' }}>
                                {data.successRate}%
                              </span>
                            </td>
                            <td>
                              <span className={`performance-badge ${data.performance.toLowerCase()}`}>
                                {data.performance}
                              </span>
                            </td>
                            <td>{new Date(data.lastExecuted).toLocaleTimeString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">No performance metrics recorded yet.</div>
                )}
              </div>
            )}

            {/* API Metrics Tab */}
            {activeTab === 'api' && (
              <div className="metrics-table-container">
                <h3>🌐 API Performance Metrics</h3>
                {pageMetrics.apiMetrics && Object.keys(pageMetrics.apiMetrics).length > 0 ? (
                  <div className="table-wrapper">
                    <table className="metrics-table">
                      <thead>
                        <tr>
                          <th>API Operation</th>
                          <th>Count</th>
                          <th>Average (ms)</th>
                          <th>Min (ms)</th>
                          <th>Max (ms)</th>
                          <th>Success Rate (%)</th>
                          <th>Performance</th>
                          <th>Last Executed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pageMetrics.apiMetrics).map(([label, data]) => (
                          <tr key={label}>
                            <td className="operation-name">{label}</td>
                            <td>{data.count}</td>
                            <td style={{ color: getPerformanceColor(parseFloat(data.average)) }}>
                              {data.average}
                            </td>
                            <td>{data.min}</td>
                            <td>{data.max}</td>
                            <td>
                              <span style={{ color: parseFloat(data.successRate) >= 95 ? '#10B981' : '#EF4444' }}>
                                {data.successRate}%
                              </span>
                            </td>
                            <td>
                              <span className={`performance-badge ${data.performance.toLowerCase()}`}>
                                {data.performance}
                              </span>
                            </td>
                            <td>{new Date(data.lastExecuted).toLocaleTimeString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">No API metrics recorded yet.</div>
                )}
              </div>
            )}

            {/* Frontend Metrics Tab */}
            {activeTab === 'frontend' && (
              <div className="metrics-table-container">
                <h3>⚡ Frontend Performance Metrics</h3>
                {pageMetrics.frontendMetrics && Object.keys(pageMetrics.frontendMetrics).length > 0 ? (
                  <div className="table-wrapper">
                    <table className="metrics-table">
                      <thead>
                        <tr>
                          <th>Operation</th>
                          <th>Count</th>
                          <th>Average (ms)</th>
                          <th>Min (ms)</th>
                          <th>Max (ms)</th>
                          <th>Success Rate (%)</th>
                          <th>Performance</th>
                          <th>Last Executed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pageMetrics.frontendMetrics).map(([label, data]) => (
                          <tr key={label}>
                            <td className="operation-name">{label}</td>
                            <td>{data.count}</td>
                            <td style={{ color: getPerformanceColor(parseFloat(data.average)) }}>
                              {data.average}
                            </td>
                            <td>{data.min}</td>
                            <td>{data.max}</td>
                            <td>
                              <span style={{ color: parseFloat(data.successRate) >= 95 ? '#10B981' : '#EF4444' }}>
                                {data.successRate}%
                              </span>
                            </td>
                            <td>
                              <span className={`performance-badge ${data.performance.toLowerCase()}`}>
                                {data.performance}
                              </span>
                            </td>
                            <td>{new Date(data.lastExecuted).toLocaleTimeString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">No frontend metrics recorded yet.</div>
                )}
              </div>
            )}

            {/* Export Tab */}
            {activeTab === 'export' && (
              <div className="export-section">
                <h3>📤 Export Performance Data</h3>
                <div className="export-controls">
                  <div className="export-format">
                    <label>Export Format:</label>
                    <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                      <option value="json">JSON</option>
                      <option value="csv">CSV</option>
                    </select>
                  </div>
                  <button onClick={exportPerformanceData} className="export-btn">
                    📥 Download Report
                  </button>
                </div>
                <div className="export-preview">
                  <h4>Preview (Page Metrics):</h4>
                  <pre className="json-preview">
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(pageMetrics.pages || {}).slice(0, 3)
                      ), 
                      null, 
                      2
                    )}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Quick Commands */}
          <div className="quick-commands">
            <h4>💡 Quick Console Commands:</h4>
            <div className="command-list">
              <code>pagePerformanceMonitor.logPageReport()</code> - View detailed page report
              <code>performanceMonitor.logReport()</code> - View detailed console report
              <code>pagePerformanceMonitor.clearPageMetrics()</code> - Clear page metrics
              <code>performanceMonitor.clearMetrics()</code> - Clear all metrics
              <code>pagePerformanceMonitor.exportPageMetrics()</code> - Export page data
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  function showPageDetails(pageName, data) {
    console.group(`📄 Detailed Metrics for ${pageName}`);
    console.table({
      'Page Name': pageName,
      'Load Time': `${data.loadTime.toFixed(2)}ms`,
      'Frontend Duration': `${data.frontendDuration.toFixed(2)}ms`,
      'API Duration': `${data.apiDuration.toFixed(2)}ms`,
      'Total Duration': `${data.totalDuration.toFixed(2)}ms`,
      'Performance': data.performance,
      'Interactions': data.interactions,
      'API Calls': data.apiCalls,
      'Errors': data.errors,
      'Last Visited': new Date(data.lastVisited).toLocaleString()
    });
    
    if (data.apiPerformance) {
      console.group('API Performance');
      console.table(data.apiPerformance);
      console.groupEnd();
    }
    
    if (data.interactionTypes) {
      console.group('Interaction Types');
      console.table(data.interactionTypes);
      console.groupEnd();
    }
    
    console.groupEnd();
  }
}

export default PerformanceDashboardPage;
